/**
 * RecentErrorsList - List of recent execution errors across all connectors
 *
 * Displays recent errors from all connectors with connector name and timestamp.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { XCircle, ExternalLink } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import type { RecentError } from '@/hooks/useConnectorStats';

interface RecentErrorsListProps {
  errors: RecentError[];
  isLoading: boolean;
  onViewDetails?: (executionId: string, connectorType: string, connectorName: string) => void;
}

/**
 * Format connector name for display
 */
function formatConnectorName(name: string): string {
  return name
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Truncate error message with ellipsis
 */
function truncateMessage(message: string, maxLength: number = 100): string {
  if (message.length <= maxLength) return message;
  return message.slice(0, maxLength) + '...';
}

/**
 * RecentErrorsList component
 */
export function RecentErrorsList({
  errors,
  isLoading,
  onViewDetails,
}: RecentErrorsListProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Errors</CardTitle>
          <CardDescription>Latest execution failures across all connectors</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-start gap-3 p-3 border rounded-lg animate-pulse">
                <div className="h-5 w-5 bg-muted rounded" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!errors || errors.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Errors</CardTitle>
          <CardDescription>Latest execution failures across all connectors</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-success/10 mb-3">
              <XCircle className="h-6 w-6 text-success" />
            </div>
            <p className="text-sm text-muted-foreground">No recent errors</p>
            <p className="text-xs text-muted-foreground mt-1">
              All connectors are running smoothly
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent Errors</CardTitle>
        <CardDescription>
          Latest execution failures across all connectors ({errors.length} error
          {errors.length !== 1 ? 's' : ''})
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {errors.map((error) => (
            <div
              key={error.execution_id}
              className={cn(
                'flex items-start gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors',
                onViewDetails && 'cursor-pointer'
              )}
              onClick={() => onViewDetails?.(error.execution_id, error.connector_type, error.connector_name)}
            >
              {/* Error Icon */}
              <div className="flex-shrink-0 mt-0.5">
                <XCircle className="h-5 w-5 text-destructive" />
              </div>

              {/* Error Details */}
              <div className="flex-1 min-w-0">
                {/* Connector Name and Query */}
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-sm font-medium text-destructive">
                    {formatConnectorName(error.connector_name)}
                  </h4>
                  {error.query_name && (
                    <>
                      <span className="text-muted-foreground">•</span>
                      <span className="text-xs text-muted-foreground truncate">
                        {error.query_name}
                      </span>
                    </>
                  )}
                </div>

                {/* Error Message */}
                <p
                  className="text-sm text-foreground mb-1"
                  title={error.error_message}
                >
                  {truncateMessage(error.error_message)}
                </p>

                {/* Timestamp */}
                <p className="text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(error.timestamp), { addSuffix: true })}
                </p>
              </div>

              {/* View Details Button */}
              {onViewDetails && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewDetails(error.execution_id, error.connector_type, error.connector_name);
                  }}
                  title="View details"
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>

        {/* Show "View All" if there are many errors */}
        {errors.length >= 10 && (
          <div className="mt-4 text-center">
            <Button variant="outline" size="sm">
              View All Errors
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
